import { PlusIcon, TrophyIcon, TrendingUpIcon, ZapIcon, BlendIcon, EyeIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCommonNavigate } from '../common/CommonLink';
import Carousel, { CarouselProvider } from '../common/Carousel';

const CreateStrategyCard = observer(() => {
    const { navigate } = useCommonNavigate();

    const investmentIdeas = [
        {
            idea: 'AI가 뜰 것 같아',
            stocks: [
                { ticker: 'NVDA', name: '엔비디아', weight: 50 },
                { ticker: 'MSFT', name: '마이크로소프트', weight: 50 },
            ],
        },
        {
            idea: '자율주행 시대가 온다',
            stocks: [
                { ticker: 'TSLA', name: '테슬라', weight: 70 },
                { ticker: 'NIO', name: '니오', weight: 30 },
            ],
        },
        {
            idea: '안정적으로 투자하고 싶어',
            stocks: [
                { ticker: 'VOO', name: 'S&P500 ETF', weight: 60 },
                { ticker: 'BND', name: '채권 ETF', weight: 40 },
            ],
        },
    ];

    return (
        <div className="flex flex-col gap-5 pt-5 pb-1 text-center">
            {/* 메인 아이콘 */}
            <div className="flex justify-center">
                <div className="bg-primary/10 text-primary rounded-full p-6">
                    <TrophyIcon className="size-12" />
                </div>
            </div>

            {/* 제목 및 설명 */}
            <div>
                <h3 className="mb-2 text-2xl font-bold">투자 전략 만들기</h3>
                <p className="text-base-content/70 text-base leading-relaxed">
                    오를 것 같은 종목을 골라 비중을 정해보세요.
                    <br />
                    매일 수익률이 자동으로 기록됩니다.
                </p>
            </div>

            {/* 특징 목록 */}
            <div className="mx-auto flex w-80 flex-col gap-3">
                <div className="flex items-start gap-3 text-left">
                    <div className="bg-error/10 text-error mt-0.5 shrink-0 rounded-full p-1.5">
                        <ZapIcon className="size-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-base-content text-sm font-bold">1분이면 충분해요</div>
                        <div className="text-base-content/60 text-sm">
                            AI가 도와드려요. 금방 만들 수 있어요.
                        </div>
                    </div>
                </div>
                <div className="flex items-start gap-3 text-left">
                    <div className="bg-success/10 text-success mt-0.5 shrink-0 rounded-full p-1.5">
                        <TrendingUpIcon className="size-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-base-content text-sm font-bold">
                            자동으로 수익률이 기록되요
                        </div>
                        <div className="text-base-content/60 text-sm">
                            따로 계산하거나 기억할 필요 없어요.
                        </div>
                    </div>
                </div>
                <div className="flex items-start gap-3 text-left">
                    <div className="bg-warning/10 text-warning mt-0.5 shrink-0 rounded-full p-1.5">
                        <BlendIcon className="size-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-base-content text-sm font-bold">
                            여러 개 만들 수 있어요
                        </div>
                        <div className="text-base-content/60 text-sm">
                            다양한 생각을 자유롭게 조합해보세요.
                        </div>
                    </div>
                </div>
                <div className="flex items-start gap-3 text-left">
                    <div className="bg-info/10 text-info mt-0.5 shrink-0 rounded-full p-1.5">
                        <EyeIcon className="size-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-base-content text-sm font-bold">
                            다른 투자자의 전략도 볼 수 있어요
                        </div>
                        <div className="text-base-content/60 text-sm">
                            수익률을 비교하고 좋은 전략을 얻어보세요.
                        </div>
                    </div>
                </div>
            </div>

            {/* 투자 아이디어 힌트 */}
            <div className="bg-base-200 -mx-5 py-4">
                <div className="mb-3 px-5 text-center">
                    <div className="text-base-content/80 text-sm font-bold">
                        💡 이런 아이디어로 시작해보세요
                    </div>
                </div>

                <CarouselProvider emblaOptions={{ loop: true }}>
                    <Carousel>
                        <Carousel.Container className="flex">
                            {investmentIdeas.map((item, index) => (
                                <Carousel.Slide key={index} className="px-5">
                                    <div className="bg-base-100 mx-2 my-1 rounded-lg p-4 shadow-xs">
                                        <div className="text-base-content mb-3 text-sm font-bold">
                                            "{item.idea}"
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {item.stocks.map((stock) => (
                                                <div
                                                    key={stock.ticker}
                                                    className="flex flex-col gap-1"
                                                >
                                                    <div className="flex items-baseline justify-between">
                                                        <div className="flex items-baseline gap-1.5">
                                                            <span className="text-base-content text-xs font-bold">
                                                                {stock.ticker}
                                                            </span>
                                                            <span className="text-base-content/60 text-xs">
                                                                {stock.name}
                                                            </span>
                                                        </div>
                                                        <span className="text-base-content text-xs font-bold tabular-nums">
                                                            {stock.weight}%
                                                        </span>
                                                    </div>
                                                    <div className="bg-base-300 h-2 w-full overflow-hidden rounded-full">
                                                        <div
                                                            className="bg-primary h-full rounded-full"
                                                            style={{ width: `${stock.weight}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Carousel.Slide>
                            ))}
                        </Carousel.Container>
                    </Carousel>
                    <Carousel.Dots className="mt-3" />
                </CarouselProvider>
            </div>

            {/* CTA 버튼 */}
            <div>
                <button
                    className="btn btn-primary btn-lg btn-block"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/strategy/create');
                    }}
                >
                    <PlusIcon className="size-6" />
                    전략 만들어보기
                </button>
            </div>
        </div>
    );
});

export default CreateStrategyCard;
